import { action, makeObservable, observable, runInAction } from "mobx";
import { randmarApiWithTags } from "randmar-api-client";
import { store } from "../../new-store/store";
import { Category } from "./Category";

class CategoriesStore {
  categories: Category[] = [];
  category?: Category = undefined;

  constructor() {
    makeObservable(this, {
      categories: observable,
      category: observable,
      getCategories: action.bound,
      getCategory: action.bound,
      updateCategoryProperty: action.bound,
      createCategory: action.bound,
      deleteCategory: action.bound,
      resetCategory: action.bound,
      starCategory: action.bound,
      uploadProductTypeImage: action.bound
    });
  }

  async getCategories(applicationId: string) {
    try {
      const info = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdCategories.initiate({
          routeManufacturerId: applicationId,
        })
      ).unwrap();
      const categories = info.map(Category.convertFrom);

      runInAction(() => {
        this.categories = categories;
      });
    } catch (e) {
      console.error(e);
    }
  }

  async getCategory(applicationId: string, categoryCode: string) {
    try {
      const info = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCode.initiate({
          routeManufacturerId: applicationId,
          categoryCode,
        })
      ).unwrap();
      const category = Category.convertFrom(info);

      runInAction(() => {
        this.category = category;
      });
    } catch (e) {
      console.error(e);
    }
  }

  async updateCategoryProperty(
    applicationId: string,
    categoryCode: string,
    fieldId: string,
    newValue: string
  ): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldName.initiate({
          routeManufacturerId: applicationId,
          categoryCode,
          fieldName: fieldId,
          newValue,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async createCategory(applicationId: string, productType: string): Promise<string> {
    try {
      return await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCode.initiate({
          routeManufacturerId: applicationId,
          categoryCode: productType,
        })
      ).unwrap();
    } catch (e) {
      console.error(e);
      return "";
    }
  }

  async deleteCategory(applicationId: string, categoryCode: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.deleteV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCode.initiate({
          routeManufacturerId: applicationId,
          categoryCode,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async resetCategory(applicationId: string, categoryCode: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeReset.initiate({
          routeManufacturerId: applicationId,
          categoryCode,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async starCategory(applicationId: string, categoryCode: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeStarred.initiate({
          routeManufacturerId: applicationId,
          categoryCode,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async uploadProductTypeImage(applicationId: string, categoryName: string, selectedFile: File): Promise<boolean> {
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeImage.initiate({
          routeManufacturerId: applicationId,
          categoryCode: categoryName,
          body: formData as unknown as never,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }
}

export default CategoriesStore;
